import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import API_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import PackingSlipPDF from '../../components/PackingSlipPDF';
import { evaluateBillingContext, type OrderBillingContext } from '../../utils/orderInvoiceRules';
import OrderBillingBanner from '../../components/OrderBillingBanner';

/* ─── Constants ─────────────────────────────────────── */
const STATUS_COLOR: Record<string, string> = {
  Draft: '#6b7280', Pending: '#6b7280',
  Confirmed: '#3b82f6', Processing: '#f59e0b',
  Shipped: '#8b5cf6', Delivered: '#8b5cf6',
  Completed: '#10b981', Cancelled: '#ef4444',
  'Pending Payment': '#f59e0b',
  'Partial Payment': '#8b5cf6',
};
const FULFIL_COLOR: Record<string, string> = {
  Unfulfilled: '#6b7280', 'Partially Shipped': '#f59e0b', Shipped: '#10b981',
};

// Editing rules — locked once fulfilment has started/ended
const EDIT_LOCKED   = ['Confirmed', 'Shipped', 'Delivered', 'Completed', 'Cancelled'];
const EDIT_LIMITED  = ['Processing']; // can still edit notes/fulfillment
const EDIT_OPEN     = ['Draft', 'Pending', 'Confirmed'];

const STAGES = ['Draft', 'Confirmed', 'Processing', 'Shipped', 'Completed'];

/* ─── Helpers ────────────────────────────────────────── */
const fmt    = (v: any) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtGbp = (v: any) => `£${Number(v || 0).toFixed(2)}`;
const toN    = (v: any) => isFinite(Number(v)) ? Number(v) : 0;

/* ══════════════════════════════════════════════════════ */
const OrderView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [order, setOrder]               = useState<any>(null);
  const [linkedInvoice, setLinkedInvoice] = useState<any>(null);
  const [billingContext, setBillingContext] = useState<OrderBillingContext>(() => evaluateBillingContext(null));
  const [loading, setLoading]           = useState(true);
  const [converting, setConverting]     = useState(false);
  const [advancing, setAdvancing]       = useState(false);
  const [confirmingDeposit, setConfirmingDeposit] = useState(false);
  const [depositConfirmed, setDepositConfirmed]   = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    Promise.all([
      apiFetch(`${API_URL}/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject(`${r.status}`)),
      apiFetch(`${API_URL}/invoices?order_id=${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([ord, invData]) => {
        setOrder(ord);
        setDepositConfirmed(!!ord?.deposit_confirmed);
        const arr = Array.isArray(invData) ? invData : (invData?.invoices || []);
        const match = ord?.linkedInvoice
          || arr.find((inv: any) =>
            String(inv.source_order_id || inv.order_id || inv.orderId) === String(id)
          )
          || (arr.length === 1 ? arr[0] : null);
        setLinkedInvoice(match || null);
        setBillingContext(ord?.billingContext || evaluateBillingContext(match));
      })
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  if (loading) return <div style={s.spinner}>⏳ Loading order…</div>;
  if (!order)  return <div style={s.spinner}>❌ Order not found</div>;

  const g = (snake: string, camel?: string) => order[snake] ?? (camel ? order[camel] : undefined);

  /* Fields */
  const status           = order.status || 'Pending';
  const statusClr        = STATUS_COLOR[status] || '#6b7280';
  const fulfillStatus    = g('fulfillment_status', 'fulfillmentStatus') || 'Unfulfilled';
  const orderNumber      = g('order_number', 'orderNumber') || order.id;
  const customerName     = g('customer_name', 'customerName') || 'Guest';
  const customerEmail    = g('customer_email', 'customerEmail');
  const customerPhone    = g('customer_phone', 'customerPhone');
  const billingAddress   = g('billing_address', 'billingAddress');
  const shippingAddress  = g('shipping_address', 'shippingAddress');
  const orderDate        = g('order_date', 'orderDate');
  const orderSource      = g('order_source', 'orderSource') || 'Online';
  const paymentMethod    = g('payment_method', 'paymentMethod');
  const depositAmount    = toN(g('deposit_amount', 'depositAmount'));
  const deliveryMethod   = g('delivery_method', 'deliveryMethod');
  const deliveryDate     = g('delivery_date', 'deliveryDate');
  const trackingNumber   = g('tracking_number', 'trackingNumber');
  const assignedStaff    = g('assigned_staff', 'assignedStaff');
  const internalNotes    = g('internal_notes', 'internalNotes');
  const priority         = order.priority || 'Normal';
  const notes            = order.notes || order.description || '';
  const items: any[]     = order.items || [];

  const subtotal   = toN(order.subtotal);
  const discVal    = toN(g('discount_value', 'discountValue'));
  const discType   = g('discount_type', 'discountType') || 'fixed';
  const taxRate    = toN(g('tax_rate', 'taxRate'));
  const shipping   = toN(g('shipping_cost', 'shippingCost'));
  const grandTotal = toN(order.total);

  // recalc robust totals — handle all field-name variants (snake_case, camelCase, shop format)
  const linesSubtotal = items.reduce((s, it) => {
    const qty   = toN(it.qty ?? it.quantity);
    const price = toN(it.price ?? it.unit_price ?? it.unitPrice);
    const disc  = toN(it.discount_pct ?? it.discountPct ?? it.discount);
    return s + qty * price * (1 - disc / 100);
  }, 0);
  const displaySubtotal = subtotal > 0 ? subtotal : linesSubtotal;
  const orderDiscount   = discType === 'percentage'
    ? (displaySubtotal * discVal) / 100 : discVal;
  const vatAmt          = (displaySubtotal - orderDiscount) * taxRate / 100;
  const displayTotal    = grandTotal > 0 ? grandTotal : (displaySubtotal - orderDiscount + vatAmt + shipping);

  /* Editing permissions */
  const locked  = EDIT_LOCKED.includes(status);
  const limited = EDIT_LIMITED.includes(status);

  /* Timeline */
  const currentStageIdx = (() => {
    const idx = STAGES.indexOf(status);
    if (idx !== -1) return idx;
    if (status === 'Delivered') return 3;
    if (status === 'Pending') return 0;
    if (status === 'Cancelled') return -1;
    return 0;
  })();

  /* Actions */
  const advanceStatus = async (newStatus: string) => {
    if (!window.confirm(`Mark this order as "${newStatus}"?`)) return;
    setAdvancing(true);
    try {
      const payload = {
        customerName:   g('customer_name', 'customerName') || '',
        customerEmail:  g('customer_email', 'customerEmail') || null,
        customerPhone:  g('customer_phone', 'customerPhone') || null,
        billingAddress: g('billing_address', 'billingAddress') || null,
        shippingAddress: g('shipping_address', 'shippingAddress') || null,
        orderSource:    g('order_source', 'orderSource') || 'Online',
        paymentMethod:  g('payment_method', 'paymentMethod') || 'Card',
        paymentStatus:  g('payment_status', 'paymentStatus') || 'Pending',
        currency:       order.currency || 'GBP',
        items:          order.items || [],
        orderDate:      (g('order_date', 'orderDate') || '').split('T')[0] || new Date().toISOString().split('T')[0],
        status:         newStatus,
        notes:          order.notes || order.description || '',
        total:          Number(order.total) || 0,
        subtotal:       Number(order.subtotal) || 0,
        deliveryMethod: g('delivery_method', 'deliveryMethod') || 'Standard',
        deliveryDate:   g('delivery_date', 'deliveryDate') || null,
        trackingNumber: g('tracking_number', 'trackingNumber') || null,
        assignedStaff:  g('assigned_staff', 'assignedStaff') || null,
        internalNotes:  g('internal_notes', 'internalNotes') || null,
        priority:       order.priority || 'Normal',
        discountType:   g('discount_type', 'discountType') || 'percentage',
        discountValue:  Number(g('discount_value', 'discountValue')) || 0,
        taxRate:        Number(g('tax_rate', 'taxRate')) || 20,
        shippingCost:   Number(g('shipping_cost', 'shippingCost')) || 0,
        description:    order.description || null,
      };
      const res = await apiFetch(`${API_URL}/orders/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const updated = await res.json();
        setOrder(updated);
        showToast(`✅ Order marked as ${newStatus}`);
      } else {
        showToast('❌ Failed to update status');
      }
    } catch { showToast('❌ Network error'); }
    finally { setAdvancing(false); }
  };

  const generateInvoice = async () => {
    if (!window.confirm('Convert this order to an invoice? A new draft invoice will be created.')) return;
    setConverting(true);
    try {
      const res = await apiFetch(`${API_URL}/orders/${id}/generate-invoice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const inv = data.invoice || data;
        const newId = inv?.id || data.id;
        if (data.existing) showToast('ℹ️ Invoice already exists — opening it');
        navigate(newId ? `/invoices/view/${newId}` : '/invoices/list');
      } else {
        showToast('❌ Failed to generate invoice');
      }
    } catch { showToast('❌ Network error'); }
    finally { setConverting(false); }
  };

  /* ══ Render ══════════════════════════════════════════ */
  return (
    <div style={s.page}>
      {/* ── Header ────────────────────────────── */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={s.title}>📦 Order #{orderNumber}</h1>
          <span style={{ padding: '5px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: statusClr + '22', color: statusClr, border: `2px solid ${statusClr}` }}>
            {status}
          </span>
          {priority !== 'Normal' && (
            <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: priority === 'Urgent' || priority === 'High' ? '#fef2f2' : '#fefce8', color: priority === 'Urgent' ? '#ef4444' : priority === 'High' ? '#f59e0b' : '#10b981', border: `1.5px solid ${priority === 'Urgent' ? '#ef4444' : '#f59e0b'}` }}>
              ⚡ {priority}
            </span>
          )}
          {order.has_backordered && (
            <span
              title="One or more items were backordered at confirmation"
              onClick={() => navigate(`/stock-movements?q=${encodeURIComponent(String(orderNumber))}`)}
              style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: '#fff7ed', color: '#c2410c', border: '1.5px solid #fdba74', cursor: 'pointer' }}
            >
              ⚠️ Backordered
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {!locked && (
            <button onClick={() => navigate(`/orders/edit/${id}`)} style={s.btnEdit}>
              {billingContext.hasInvoice ? '📦 Update delivery & status' : '✏️ Edit Order'}
            </button>
          )}
          {billingContext.canEditInvoiceLines && billingContext.invoiceId && (
            <button onClick={() => navigate(`/invoices/edit/${billingContext.invoiceId}`)} style={s.btnPurple}>
              🧾 Edit Invoice
            </button>
          )}
          <button onClick={() => navigate('/orders/list')} style={s.btnBack}>← Back</button>
        </div>
      </div>

      <OrderBillingBanner billing={billingContext} />

      {/* ── Edit-lock Banner ───────────────────── */}
      {locked && (
        <div style={{ marginBottom: 20, padding: '14px 20px', borderRadius: 12, background: '#fef2f2', border: '2px solid #ef4444', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>🔒</span>
          <div>
            <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 15 }}>Order Locked — Editing Disabled</div>
            <div style={{ color: '#dc2626', fontSize: 13, marginTop: 2 }}>
              Orders with status <strong>{status}</strong> cannot be edited. Contact your administrator if changes are required.
            </div>
          </div>
        </div>
      )}
      {limited && (
        <div style={{ marginBottom: 20, padding: '14px 20px', borderRadius: 12, background: '#fffbeb', border: '2px solid #f59e0b', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: 15 }}>Limited Editing — Processing in Progress</div>
            <div style={{ color: '#b45309', fontSize: 13, marginTop: 2 }}>
              Items and customer details are locked. You can still update notes, tracking numbers, and fulfillment status.
            </div>
          </div>
        </div>
      )}

      <div style={s.card}>
        {/* ── Summary Banner ─────────────────── */}
        <div style={{ ...s.summaryBanner, background: statusClr }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{customerName}</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {orderDate && <span>📅 {fmt(orderDate)}</span>}
              <span>📋 {orderSource}</span>
              {linkedInvoice && (
                <span
                  onClick={() => navigate(`/invoices/view/${linkedInvoice.id}`)}
                  style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '1px 10px' }}
                >
                  🧾 Invoice #{linkedInvoice.invoice_number || linkedInvoice.id?.slice(-6).toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' as const }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{fmtGbp(displayTotal)}</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, marginTop: 2 }}>{items.length} item{items.length !== 1 ? 's' : ''}</div>
          </div>
        </div>

        {/* ── Quick Actions ──────────────────── */}
        <div style={s.quickBar}>
          {linkedInvoice ? (
            <button onClick={() => navigate(`/invoices/view/${linkedInvoice.id}`)} style={s.qBtn('#6366f1')}>
              🧾 View Invoice #{linkedInvoice.invoice_number || linkedInvoice.id?.slice(-6).toUpperCase()}
            </button>
          ) : (
            status !== 'Cancelled' && (
              <button onClick={generateInvoice} disabled={converting} style={s.qBtn('#10b981')}>
                {converting ? '⏳ Creating…' : '🧾 Generate Invoice'}
              </button>
            )
          )}
          {trackingNumber && (
            <a href={`https://www.google.com/search?q=${encodeURIComponent(trackingNumber)}`} target="_blank" rel="noreferrer" style={s.qLink}>
              🔍 Track Shipment
            </a>
          )}
          <PDFDownloadLink
            document={<PackingSlipPDF order={order} />}
            fileName={`packing-slip-${orderNumber}.pdf`}
            style={{ ...s.qBtn('#f59e0b'), display: 'inline-flex', alignItems: 'center' }}
          >
            {({ loading: pdfLoading }: any) => (
              <span>{pdfLoading ? '⏳ Preparing…' : '📋 Packing Slip'}</span>
            )}
          </PDFDownloadLink>
          <button onClick={() => window.open(`${API_URL}/orders/${id}/export-pdf`, '_blank')} style={s.qBtn('#6b7280')}>
            🖨️ Print Order
          </button>
        </div>

        {/* ── Status Actions ─────────────────── */}
        {status !== 'Cancelled' && status !== 'Completed' && (() => {
          const nextMap: Record<string, string> = {
            Draft: 'Confirmed', Pending: 'Confirmed',
            Confirmed: 'Processing', Processing: 'Shipped', Shipped: 'Completed',
          };
          const nextStatus = nextMap[status];
          const nextColors: Record<string, string> = {
            Confirmed: '#3b82f6', Processing: '#f59e0b', Shipped: '#8b5cf6', Completed: '#10b981',
          };
          const nextClr = nextStatus ? (nextColors[nextStatus] || '#10b981') : '#10b981';
          return (
            <div style={{ padding: '12px 20px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Quick Actions:</span>
              {nextStatus && (
                <button
                  disabled={advancing}
                  onClick={() => advanceStatus(nextStatus)}
                  style={{ padding: '8px 18px', background: advancing ? '#9ca3af' : nextClr, color: '#fff', border: 'none', borderRadius: 8, cursor: advancing ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}
                >
                  {advancing ? '⏳ Updating…' : `→ Mark as ${nextStatus}`}
                </button>
              )}
              <button
                disabled={advancing}
                onClick={() => advanceStatus('Cancelled')}
                style={{ padding: '8px 18px', background: '#fff', color: '#ef4444', border: '2px solid #ef4444', borderRadius: 8, cursor: advancing ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}
              >
                ✕ Cancel Order
              </button>
            </div>
          );
        })()}

        {/* ── Progress Timeline ──────────────── */}
        {status !== 'Cancelled' && (
          <div style={s.section}>
            <h3 style={s.sTitle}>Order Progress</h3>
            <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', padding: '4px 0 12px' }}>
              {STAGES.map((stage, idx) => {
                const done    = currentStageIdx === -1 ? false : idx < currentStageIdx;
                const current = currentStageIdx === -1 ? false : idx === currentStageIdx;
                const stClr   = done ? '#10b981' : current ? statusClr : '#d1d5db';
                return (
                  <div key={stage} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative', minWidth: 76 }}>
                    {idx < STAGES.length - 1 && (
                      <div style={{ position: 'absolute', top: 13, left: '50%', width: '100%', height: 3, background: done ? '#10b981' : '#e5e7eb', zIndex: 1 }} />
                    )}
                    <div style={{ width: 28, height: 28, borderRadius: '50%', zIndex: 2, marginBottom: 8, background: stClr, border: current ? '3px solid #fff' : 'none', boxShadow: current ? `0 0 0 3px ${statusClr}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {done && <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>✓</span>}
                      {current && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>●</span>}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: current ? 700 : 400, color: done || current ? '#1f2937' : '#9ca3af', textAlign: 'center' }}>{stage}</span>
                  </div>
                );
              })}
              {/* Invoiced stage */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 76 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', marginBottom: 8, background: linkedInvoice ? '#6366f1' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {linkedInvoice && <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>✓</span>}
                </div>
                <span style={{ fontSize: 12, color: linkedInvoice ? '#6366f1' : '#9ca3af', fontWeight: linkedInvoice ? 700 : 400 }}>Invoiced</span>
              </div>
            </div>
          </div>
        )}
        {status === 'Cancelled' && (
          <div style={{ ...s.section, background: '#fef2f2' }}>
            <div style={{ fontWeight: 700, color: '#ef4444', fontSize: 15 }}>❌ Order Cancelled</div>
            <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 4 }}>This order has been cancelled and is no longer active.</div>
          </div>
        )}

        {/* ── Customer ──────────────────────────── */}
        <div style={s.section}>
          <h3 style={s.sTitle}>👤 Customer</h3>
          <div style={s.grid}>
            <F label="Name"  value={customerName} />
            {customerEmail && <F label="Email" value={customerEmail} href={`mailto:${customerEmail}`} />}
            {customerPhone && <F label="Phone" value={customerPhone} href={`tel:${customerPhone}`} />}
          </div>
          <div style={s.addrRow}>
            {billingAddress && (
              <div style={{ flex: 1 }}>
                <span style={s.fl}>Billing Address</span>
                <div style={s.addrBox}>{billingAddress}</div>
              </div>
            )}
            {shippingAddress && shippingAddress !== billingAddress && (
              <div style={{ flex: 1 }}>
                <span style={s.fl}>Shipping Address</span>
                <div style={s.addrBox}>{shippingAddress}</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Order Details ───────────────────── */}
        <div style={s.section}>
          <h3 style={s.sTitle}>📋 Order Details</h3>
          <div style={s.grid}>
            <F label="Order Number"  value={`#${orderNumber}`} />
            <F label="Order Date"    value={fmt(orderDate)} />
            <F label="Source"        value={orderSource} />
            {paymentMethod && <F label="Payment Method" value={paymentMethod} />}
            {deliveryDate && <F label="Delivery Date" value={fmt(deliveryDate)} />}
          </div>
          {notes && (
            <div style={{ marginTop: 14 }}>
              <span style={s.fl}>Notes</span>
              <div style={s.notesBox}>{notes}</div>
            </div>
          )}
        </div>

        {/* ── Fulfillment ─────────────────────── */}
        <div style={s.section}>
          <h3 style={s.sTitle}>🚚 Fulfillment</h3>
          {/* Fulfillment status pills */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {(['Unfulfilled', 'Partially Shipped', 'Shipped'] as const).map(fs => {
              const clr = FULFIL_COLOR[fs];
              const active = fulfillStatus === fs;
              return (
                <span key={fs} style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: active ? clr : 'transparent', color: active ? '#fff' : clr, border: `2px solid ${clr}` }}>
                  {fs}
                </span>
              );
            })}
          </div>
          <div style={s.grid}>
            {deliveryMethod && <F label="Delivery Method" value={deliveryMethod} />}
            {trackingNumber  && <F label="Tracking #"      value={trackingNumber} />}
          </div>
        </div>

        {/* ── Order Items ─────────────────────── */}
        <div style={s.section}>
          <h3 style={s.sTitle}>📦 Items</h3>
          {items.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={s.th}>Product</th>
                    <th style={s.th}>Description</th>
                    <th style={{ ...s.th, textAlign: 'center' as const }}>Unit</th>
                    <th style={{ ...s.th, textAlign: 'center' as const }}>Qty</th>
                    <th style={{ ...s.th, textAlign: 'right' as const }}>Price</th>
                    <th style={{ ...s.th, textAlign: 'center' as const }}>Disc%</th>
                    <th style={{ ...s.th, textAlign: 'right' as const }}>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: any, i: number) => {
                    const qty    = toN(item.qty ?? item.quantity);
                    const price  = toN(item.price ?? item.unit_price ?? item.unitPrice);
                    const disc   = toN(item.discount_pct ?? item.discountPct ?? item.discount);
                    const gross  = qty * price;
                    const net    = gross * (1 - disc / 100);
                    const unit   = item.unit || 'pcs';
                    const descr  = item.description || '';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={s.td}><strong style={{ color: '#1f2937' }}>{item.productName || item.product_name || item.name || '—'}</strong></td>
                        <td style={{ ...s.td, color: '#6b7280', fontSize: 13 }}>{descr || '—'}</td>
                        <td style={{ ...s.td, textAlign: 'center' as const, color: '#6b7280', fontSize: 13 }}>{unit}</td>
                        <td style={{ ...s.td, textAlign: 'center' as const }}>{qty}</td>
                        <td style={{ ...s.td, textAlign: 'right' as const }}>{fmtGbp(price)}</td>
                        <td style={{ ...s.td, textAlign: 'center' as const }}>
                          {disc > 0 ? (
                            <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 7px', fontSize: 12, fontWeight: 600 }}>{disc}%</span>
                          ) : '—'}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right' as const }}>
                          {disc > 0 && <div style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'line-through' }}>{fmtGbp(gross)}</div>}
                          <strong style={{ color: '#10b981' }}>{fmtGbp(net)}</strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>No items recorded</p>
          )}

          {/* Totals */}
          <div style={s.totals}>
            <TR label="Subtotal"  value={fmtGbp(displaySubtotal)} />
            {orderDiscount > 0 && <TR label={`Discount${discType === 'percentage' ? ` (${discVal}%)` : ''}`} value={`— ${fmtGbp(orderDiscount)}`} red />}
            {taxRate > 0         && <TR label={`VAT (${taxRate}%)`} value={fmtGbp(vatAmt)} />}
            {shipping > 0        && <TR label="Shipping" value={fmtGbp(shipping)} />}
            <TR label="ORDER TOTAL" value={fmtGbp(displayTotal)} grand />
            {(paymentMethod === 'Part Payment' || status === 'Partial Payment') && depositAmount > 0 && (
              <>
                <TR label={depositConfirmed ? '✅ Deposit Confirmed' : '💰 Deposit Received'} value={fmtGbp(depositAmount)} green />
                <TR label="⏳ Remaining Balance" value={fmtGbp(Math.max(0, displayTotal - depositAmount))} orange />
                {!depositConfirmed && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      disabled={confirmingDeposit}
                      onClick={async () => {
                        if (!window.confirm(`Record the £${depositAmount.toFixed(2)} deposit as received income in Accounting?`)) return;
                        setConfirmingDeposit(true);
                        try {
                          const r = await apiFetch(`${API_URL}/orders/${id}/confirm-deposit`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                          });
                          if (r.ok) {
                            setDepositConfirmed(true);
                            setOrder((o: any) => ({ ...o, deposit_confirmed: true }));
                            showToast('✅ Deposit recorded in Accounting.');
                          } else {
                            const err = await r.json().catch(() => ({}));
                            showToast('❌ ' + (err.error || 'Failed to confirm deposit'));
                          }
                        } catch { showToast('❌ Network error'); }
                        finally { setConfirmingDeposit(false); }
                      }}
                      style={{
                        padding: '8px 18px', borderRadius: 8, border: 'none', cursor: confirmingDeposit ? 'not-allowed' : 'pointer',
                        background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 13, opacity: confirmingDeposit ? 0.6 : 1,
                      }}
                    >
                      {confirmingDeposit ? '⏳ Confirming…' : `✅ Confirm Deposit Received (${fmtGbp(depositAmount)})`}
                    </button>
                    <p style={{ margin: '6px 0 0', fontSize: 12, color: '#6b7280' }}>Records this deposit in Accounting. Only needed once.</p>
                  </div>
                )}
                {depositConfirmed && (
                  <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600, marginTop: 8 }}>✔ Deposit recorded in Accounting</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Internal (Staff Only) ───────────── */}
        {(internalNotes || assignedStaff) && (
          <div style={{ ...s.section, background: '#fffdf0' }}>
            <h3 style={s.sTitle}>🔒 Internal — Staff Only</h3>
            <div style={s.grid}>
              {assignedStaff && <F label="Assigned Staff" value={assignedStaff} />}
              <F label="Priority" value={priority} />
            </div>
            {internalNotes && (
              <div style={{ marginTop: 12 }}>
                <span style={s.fl}>Internal Notes</span>
                <div style={{ ...s.notesBox, background: '#fff8e1' }}>{internalNotes}</div>
              </div>
            )}
          </div>
        )}

        {/* ── Action Bar ─────────────────────── */}
        <div style={s.actionBar}>
          {!locked && (
            <button onClick={() => navigate(`/orders/edit/${id}`)} style={s.btnPrimaryLg}>
              {billingContext.hasInvoice ? '📦 Update delivery & status' : '✏️ Edit Order'}
            </button>
          )}
          {!linkedInvoice && status !== 'Cancelled' && (
            <button onClick={generateInvoice} disabled={converting} style={s.btnGreenLg}>
              {converting ? '⏳ Creating…' : '🧾 Generate Invoice'}
            </button>
          )}
          {linkedInvoice && (
            <button onClick={() => navigate(`/invoices/view/${linkedInvoice.id}`)} style={s.btnPurpleLg}>
              🧾 View Invoice
            </button>
          )}
          <button onClick={() => navigate('/orders/list')} style={s.btnGreyLg}>← Back to Orders</button>
        </div>
      </div>
    </div>
  );
};

/* ─── Sub-components ─────────────────────────────────── */
const F = ({ label, value, href }: { label: string; value: any; href?: string }) => (
  <div style={{ marginBottom: 16 }}>
    <span style={{ display: 'block', fontSize: 11, color: '#6b7280', fontWeight: 700, marginBottom: 3, textTransform: 'uppercase' as const, letterSpacing: '0.6px' }}>{label}</span>
    {href ? (
      <a href={href} style={{ color: '#3b82f6', fontSize: 15, fontWeight: 500 }}>{value || '—'}</a>
    ) : (
      <span style={{ fontSize: 15, color: '#1f2937', fontWeight: 500 }}>{value ?? '—'}</span>
    )}
  </div>
);

const TR = ({ label, value, red, grand, green, orange }: { label: string; value: string; red?: boolean; grand?: boolean; green?: boolean; orange?: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: grand ? '12px 0 4px' : '7px 0', fontSize: grand ? 17 : 14, fontWeight: grand ? 700 : (green || orange ? 700 : 400), borderTop: grand ? '2px solid #1f2937' : '1px solid #e5e7eb', color: red ? '#ef4444' : grand ? '#10b981' : green ? '#059669' : orange ? '#b45309' : '#4b5563' }}>
    <span>{label}</span><span>{value}</span>
  </div>
);

/* ─── Styles ─────────────────────────────────────────── */
const s: any = {
  page:       { maxWidth: 960, margin: '30px auto', padding: '0 16px', fontFamily: 'system-ui,-apple-system,sans-serif' },
  spinner:    { padding: 60, textAlign: 'center', fontSize: 18 },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title:      { margin: 0, fontSize: 26, fontWeight: 700, color: '#1f2937' },
  btnEdit:    { padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  btnPurple:  { padding: '10px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  btnBack:    { padding: '10px 20px', background: '#f3f4f6', border: '1.5px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#374151' },
  card:       { background: '#fff', borderRadius: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.09)', overflow: 'hidden', marginBottom: 30 },
  summaryBanner: { padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 },
  quickBar:   { padding: '12px 20px', background: '#f9fafb', display: 'flex', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid #e5e7eb' },
  qBtn:       (clr: string) => ({ padding: '9px 18px', background: '#fff', color: clr, border: `2px solid ${clr}`, borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, textDecoration: 'none' }),
  qLink:      { padding: '9px 18px', background: '#fff', color: '#3b82f6', border: '2px solid #3b82f6', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' },
  section:    { padding: '20px 24px', borderTop: '1px solid #f0f0f0' },
  sTitle:     { margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: '#1f2937' },
  grid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0 28px' },
  addrRow:    { display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 14 },
  fl:         { display: 'block', fontSize: 11, color: '#6b7280', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.6px' },
  addrBox:    { background: '#f9fafb', padding: '10px 14px', borderRadius: 8, fontSize: 14, color: '#374151', whiteSpace: 'pre-line', lineHeight: 1.7, border: '1px solid #e5e7eb' },
  notesBox:   { background: '#f9fafb', padding: '12px 16px', borderRadius: 8, fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', border: '1px solid #e5e7eb', marginTop: 4 },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th:         { padding: '10px 12px', textAlign: 'left' as const, fontSize: 12, fontWeight: 700, color: '#6b7280', borderBottom: '2px solid #e5e7eb', textTransform: 'uppercase' as const, letterSpacing: '0.4px' },
  td:         { padding: '12px 12px', verticalAlign: 'middle' as const },
  totals:     { marginTop: 20, padding: '10px 16px 14px', background: '#f0fdf4', borderRadius: 10, border: '2px solid #bbf7d0', maxWidth: 360, marginLeft: 'auto' },
  actionBar:  { padding: '20px 24px', background: '#f9fafb', display: 'flex', gap: 12, justifyContent: 'center', borderTop: '1px solid #e5e7eb', flexWrap: 'wrap' },
  btnPrimaryLg: { padding: '12px 26px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 },
  btnGreenLg:   { padding: '12px 26px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 },
  btnPurpleLg:  { padding: '12px 26px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 },
  btnGreyLg:    { padding: '12px 26px', background: '#fff', color: '#6b7280', border: '1.5px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15 },
};

export default OrderView;
